import { useMemo, useState } from 'react';
import {
  ASSESSMENT_TEMPLATES,
  gradeLabel,
  templatesForGrade,
  type AssessmentTemplate,
} from '@td/shared';
import { useCreateAssessment } from '../hooks/useAssessments';
import { useAuth } from '../auth/AuthContext';
import { Modal } from './Modal';

/**
 * Pick a ready-made assessment, or start from nothing.
 *
 * This used to live only in the empty state, which meant adding your first
 * assessment took the templates away with it — the one moment you are most
 * likely to want a second one.
 */
export function AddAssessmentMenu({
  existingNames,
  onBlank,
  onClose,
}: {
  existingNames: string[];
  onBlank: () => void;
  onClose: () => void;
}) {
  const create = useCreateAssessment();
  const { user } = useAuth();

  /*
   * Her grade's assessments first, the rest still reachable.
   *
   * These were shown as one flat list with no notion of grade at all, which
   * is the sort of thing that reads as fine until a first-grade teacher is
   * scrolling past a middle-school screener. Sorted rather than filtered: a
   * first-grade teacher with a child reading at a kindergarten level has a
   * real reason to reach for the kindergarten one, and a picker that hides
   * it has decided it knows better than she does.
   */
  const grade = user?.settings.gradeLevel ?? null;
  const sorted = useMemo(() => templatesForGrade(ASSESSMENT_TEMPLATES, grade), [grade]);

  const mine = sorted.filter((t) => t.matches);
  const others = sorted.filter((t) => !t.matches);

  const [showingOthers, setShowingOthers] = useState(false);

  function add(template: AssessmentTemplate) {
    create.mutate(
      {
        name: template.name,
        subject: template.subject,
        termly: template.termly,
        measures: template.measures.map((m, i) => ({
          ...m,
          id: `m-${Date.now().toString(36)}-${i}`,
        })),
      },
      { onSuccess: onClose },
    );
  }

  return (
    <Modal
      onClose={onClose}
      label="Add an assessment"
      className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-card bg-white p-5 shadow-lifted"
    >
      <div className="mb-1 flex items-start justify-between gap-2">
        <h2 className="text-lg">Add an assessment</h2>
        <button onClick={onClose} aria-label="Close" className="text-stone hover:text-espresso">
          ✕
        </button>
      </div>
      <p className="mb-4 text-xs leading-snug text-stone">
        Measures and units are filled in for you. Benchmark cut scores are left blank on purpose —
        they move by grade, term and publisher revision, so put in your own.
        {!grade && ' Set a grade level in Settings and the list leads with the ones for it.'}
      </p>

      {grade && others.length > 0 && (
        <p className="mb-2 text-[11px] uppercase tracking-wide text-stone">
          For {gradeLabel(grade)}
        </p>
      )}

      <ul className="mb-3 space-y-2">
        {mine.map(({ template }) => (
          <TemplateRow
            key={template.name}
            template={template}
            already={existingNames.includes(template.name)}
            busy={create.isPending}
            onAdd={() => add(template)}
          />
        ))}
      </ul>

      {others.length > 0 && (
        <div className="mb-4">
          {showingOthers ? (
            <>
              <p className="mb-2 text-[11px] uppercase tracking-wide text-stone">Other grades</p>
              <ul className="space-y-2">
                {others.map(({ template }) => (
                  <TemplateRow
                    key={template.name}
                    template={template}
                    already={existingNames.includes(template.name)}
                    busy={create.isPending}
                    onAdd={() => add(template)}
                  />
                ))}
              </ul>
            </>
          ) : (
            <button className="btn-soft" onClick={() => setShowingOthers(true)}>
              Show the {others.length} for other grades
            </button>
          )}
        </div>
      )}

      <div className="flex justify-between gap-2 border-t border-sand pt-3">
        <button onClick={onBlank} className="btn-secondary">
          Build my own
        </button>
        <button onClick={onClose} className="btn-soft">
          Done
        </button>
      </div>
    </Modal>
  );
}

/** One template, offered. */
function TemplateRow({
  template,
  already,
  busy,
  onAdd,
}: {
  template: AssessmentTemplate;
  already: boolean;
  busy: boolean;
  onAdd: () => void;
}) {
  return (
    <li className="flex items-center gap-3 rounded-card border border-sand px-3 py-2">
      <div className="min-w-0 flex-1">
        <p className="text-sm text-espresso">{template.name}</p>
        <p className="text-[11px] leading-snug text-stone">{template.blurb}</p>

        {/*
          Which grades it is for, said on the row. The sort already puts hers
          first; this is what stops "Other grades" being a mystery box.
        */}
        {template.grades.length > 0 && (
          <p className="mt-0.5 text-[11px] text-stone/80">
            {template.grades.length > 3
              ? `${gradeLabel(template.grades[0]!)} – ${gradeLabel(template.grades[template.grades.length - 1]!)}`
              : template.grades.map((g) => gradeLabel(g)).join(', ')}
          </p>
        )}
      </div>

      <button onClick={onAdd} disabled={busy} className="btn-soft-accent shrink-0">
        {already ? 'Add another' : 'Add it'}
      </button>
    </li>
  );
}
